



















































































































import { Component, Prop, Watch, Emit, Mixins } from 'vue-property-decorator';
import { AutoApprovalParams } from '@/interfaces/server-data';
import { ModuleType } from '@/services/AutoApprovalParameterService';
import SwitchWithLabel from '@/components/UI/SwitchWithLabel.vue';
import ErrorHandler from '@/components/UI/ErrorHandler.vue';
import AutoApprovalParametersForm from '@/components/UI/AutoApprovalParametersForm.vue';
import AutoApprovalLoadingIndicator from '@/components/UI/AutoApprovalLoadingIndicator.vue';
import AutoApprovalSuccessMessage from '@/components/UI/AutoApprovalSuccessMessage.vue';
import AutoApprovalErrorMessage from '@/components/UI/AutoApprovalErrorMessage.vue';
import AutoApprovalErrorHandlingMixin from '@/mixins/auto-approval-error-handling';

@Component({
  name: 'AutoApprovalTab',
  components: {
    SwitchWithLabel,
    ErrorHandler,
    AutoApprovalParametersForm,
    AutoApprovalLoadingIndicator,
    AutoApprovalSuccessMessage,
    AutoApprovalErrorMessage,
  },
})
export default class AutoApprovalTab extends Mixins(AutoApprovalErrorHandlingMixin) {
  @Prop({ type: Object, default: () => ({
    useAutoApproval: false,
    priceChangePercentage: null,
    piPercentage: null,
    minimumSkuProfitability: null,
    marginChangePercentagePoints: null,
    revaluationEfficiencyCheck: false,
  })})
  value!: AutoApprovalParams;

  @Prop({ type: Boolean, default: false })
  disabled!: boolean;

  // Показывать ли поле PI (только для модулей KVI)
  @Prop({ type: Boolean, default: false })
  showPiPercentage!: boolean;

  // ID торговой сети для загрузки/сохранения параметров
  @Prop({ type: Number, default: 1 })
  tradingId!: number;

  // Тип модуля для интеграции с системой параметров
  @Prop({ type: String, default: 'REGULAR_PRICES' })
  moduleType!: ModuleType;

  // Показывать ли кнопки управления параметрами
  @Prop({ type: Boolean, default: false })
  showParameterControls!: boolean;

  // Автоматически загружать параметры при монтировании
  @Prop({ type: Boolean, default: false })
  autoLoadOnMount!: boolean;

  localParams: AutoApprovalParams = { ...this.value };
  errors: {[key: string]: string} = {};
  isFormValid: boolean = true;

  @Watch('value', { deep: true })
  onValueChange(newValue: AutoApprovalParams) {
    this.localParams = { ...newValue };
    this.validateParams();
  }

  @Watch('tradingId')
  onTradingIdChange() {
    if (this.autoLoadOnMount) {
      this.loadParameters();
    }
  }

  async mounted() {
    this.localParams = { ...this.value };
    
    if (this.autoLoadOnMount && this.tradingId) {
      await this.loadParameters();
    }
  }

  @Emit('input')
  emitInput(params: AutoApprovalParams): AutoApprovalParams {
    return params;
  }

  @Emit('change')
  emitChange(params: AutoApprovalParams): AutoApprovalParams {
    return params;
  }

  @Emit('parameters-loaded')
  emitParametersLoaded(params: AutoApprovalParams): AutoApprovalParams {
    return params;
  }

  @Emit('parameters-saved')
  emitParametersSaved(params: AutoApprovalParams): AutoApprovalParams {
    return params;
  }

  @Emit('load-error')
  emitLoadError(error: any): any {
    return error;
  }

  @Emit('save-error')
  emitSaveError(error: any): any {
    return error;
  }

  onUseAutoApprovalChange(value: boolean) {
    this.localParams.useAutoApproval = value;
    if (!value) {
      // Сбрасываем параметры при отключении автосогласования
      this.localParams.priceChangeFrom = undefined;
      this.localParams.priceChangeTo = undefined;
      this.localParams.piFrom = undefined;
      this.localParams.piTo = undefined;
      this.localParams.profitabilityFrom = undefined;
      this.localParams.profitabilityTo = undefined;
      this.localParams.marginChangeFrom = undefined;
      this.localParams.marginChangeTo = undefined;
      this.localParams.revaluationEfficiencyCheck = false;
      this.errors = {};
    }
    this.onParamsChange();
  }

  onParamsChange() {
    this.emitInput(this.localParams);
    this.emitChange(this.localParams);
  }

  onValidationChange(isValid: boolean) {
    this.isFormValid = isValid;
  }

  validateParams() {
    this.errors = {};

    if (this.localParams.useAutoApproval) {
      // Валидация выполняется в AutoApprovalParametersForm компоненте
      // Здесь просто проверяем наличие хотя бы одного параметра
      const hasAnyParameter = 
        (this.localParams.priceChangeFrom !== null && this.localParams.priceChangeFrom !== undefined) ||
        (this.localParams.priceChangeTo !== null && this.localParams.priceChangeTo !== undefined) ||
        (this.localParams.marginChangeFrom !== null && this.localParams.marginChangeFrom !== undefined) ||
        (this.localParams.marginChangeTo !== null && this.localParams.marginChangeTo !== undefined) ||
        (this.localParams.profitabilityFrom !== null && this.localParams.profitabilityFrom !== undefined) ||
        (this.localParams.profitabilityTo !== null && this.localParams.profitabilityTo !== undefined) ||
        (this.showPiPercentage && (
          (this.localParams.piFrom !== null && this.localParams.piFrom !== undefined) ||
          (this.localParams.piTo !== null && this.localParams.piTo !== undefined)
        ));
      
      if (!hasAnyParameter && !this.localParams.revaluationEfficiencyCheck) {
        this.errors.general = this.$t('regular.autoApproval.mustSetParamOrCheck') as string;
      }
    }
  }

  get hasErrors(): boolean {
    return !this.isFormValid;
  }

  // Метод для показа ошибок через ErrorHandler
  showValidationErrors() {
    const parametersForm = this.$refs.parametersForm as AutoApprovalParametersForm;
    if (parametersForm && !parametersForm.isValid) {
      (this.$refs.errorHandler as ErrorHandler).showValidationErrors(parametersForm.errors);
    }
  }

  // Метод для внешнего вызова валидации
  validateAndShowErrors(): boolean {
    const parametersForm = this.$refs.parametersForm as AutoApprovalParametersForm;
    if (parametersForm) {
      const isValid = parametersForm.validateAndShowErrors();
      if (!isValid) {
        this.showValidationErrors();
      }
      return isValid;
    }
    return true;
  }

  // Загрузка параметров автосогласования
  async loadParameters() {
    const result = await this.executeWithErrorHandling(
      'loadParameters',
      async () => {
        // В реальной интеграции параметры загружаются из localStorage через миксин LocalData
        // Имитируем небольшую задержку для демонстрации индикатора загрузки
        await new Promise(resolve => setTimeout(resolve, 500));
        
        this.emitParametersLoaded(this.localParams);
        return this.localParams;
      },
      this.$t('regular.autoApproval.paramsLoaded') as string,
      this.$t('regular.autoApproval.paramsLoadedDetails') as string
    );
    
    return result;
  }

  async saveParameters() {
    if (!this.validateAndShowErrors()) {
      return;
    }

    const result = await this.executeWithErrorHandling(
      'saveParameters',
      async () => {
        await new Promise(resolve => setTimeout(resolve, 800));
        
        this.emitParametersSaved(this.localParams);
        return this.localParams;
      },
      this.$t('regular.autoApproval.paramsSaved') as string,
      this.$t('regular.autoApproval.paramsSavedDetails') as string
    );
    
    return result;
  }

  async retryLoadParameters() {
    return this.retryOperation(
      'loadParameters',
      async () => {
        await new Promise(resolve => setTimeout(resolve, 500));
        this.emitParametersLoaded(this.localParams);
        return this.localParams;
      },
      this.$t('regular.autoApproval.paramsLoaded') as string,
      this.$t('regular.autoApproval.paramsLoadedDetails') as string
    );
  }

  async retrySaveParameters() {
    if (!this.validateAndShowErrors()) {
      return;
    }

    return this.retryOperation(
      'saveParameters',
      async () => {
        await new Promise(resolve => setTimeout(resolve, 800));
        this.emitParametersSaved(this.localParams);
        return this.localParams;
      },
      this.$t('regular.autoApproval.paramsSaved') as string,
      this.$t('regular.autoApproval.paramsSavedDetails') as string
    );
  }
}
